import { getWebServicesList } from "./webServicesList";

export type SignalMessage =
	| { event: "peer-joined"; connectionId: string }
	| { event: "peer-left"; connectionId: string }
	| { event: "signal"; type: string; steamId?: string; useSteam?: boolean; sdpOrCandidate?: any };

/** Thrown by a handshake when the other party disconnects before pairing completes. */
export const PEER_LEFT = "peer-left";

interface HandshakeResult {
	useSteam: boolean;
	peerSteamId: string | null;
}

export class SignallingChannel {
	channel: WebSocket | null = null;
	private pendingReject: ((error: Error) => void) | null = null;

	constructor() { }

	async connect(lobbyCode: string, timeout = 3000): Promise<boolean> {
		const Services = await getWebServicesList();
		const uri = Services.SignallingURLTemplate.replace('[LOBBY_ID]', lobbyCode);
		const channel = new WebSocket(uri);

		channel.addEventListener("open", () => {
			console.log("Signaling websocket opened");
			channel.send(JSON.stringify({ event: 'peer-joined' }));
		});

		channel.addEventListener("error", (e) => {
			console.warn("Signaling websocket error", e);
		});

		channel.addEventListener("close", (ev) => {
			console.log("Signaling websocket closed", ev.code, ev.reason);
		});

		return new Promise<boolean>((resolve, reject) => {
			const t = setTimeout(() => {
				channel.close();
				this.channel = null;
				console.error("Signaling websocket connection timed out");
				resolve(false)
			}, timeout);
			channel.addEventListener("open", () => {
				clearTimeout(t);
				this.channel = channel;
				resolve(true);
			}, { once: true });
			channel.addEventListener("error", (error) => {
				clearTimeout(t);
				channel.close();
				this.channel = null;
				console.error("Signaling websocket connection error: " + error);
				resolve(false);
			}, { once: true });
		});
	}

	hostHandshake(localSteamId: string | null, timeoutMs: number): Promise<HandshakeResult> {
		if (!this.channel) {
			return Promise.reject(new Error("Signalling channel is disposed or not connected"));
		}
		const signalingChannel = this.channel;

		return new Promise((resolve, reject) => {
			let peerSteamId: string | null = null;
			let settled = false;
			let timer: any = null;
			let handleMessage: (event: MessageEvent) => void;

			//Every exit goes through here so the listener, the timer and pendingReject are always
			//cleaned up, and the promise settles exactly once
			const finish = (result: HandshakeResult | null, error?: Error) => {
				if (settled) return;
				settled = true;
				if (timer !== null) clearTimeout(timer);
				signalingChannel.removeEventListener("message", handleMessage);
				if (this.pendingReject === abort) this.pendingReject = null;
				if (error) reject(error); else resolve(result!);
			};
			const abort = (error: Error) => finish(null, error);
			this.pendingReject = abort;
			timer = setTimeout(() => finish(null, new Error(`Host handshake timed out after ${timeoutMs}ms`)), timeoutMs);

			handleMessage = (event: MessageEvent) => {
				if (!this.channel) {
					console.error("WebRTCConnection: Closed signalling channel during handshake, handshake aborted");
					return;
				}
				const msg: SignalMessage = JSON.parse(event.data);

				if (msg.event === "peer-left") {
					finish(null, new Error(PEER_LEFT));
					return;
				}

				if (msg.event === "peer-joined") {
					console.log("[HOST] Peer joined, sending our Steam ID:", localSteamId || "(none)");
					signalingChannel.send(JSON.stringify({
						action: "signal",
						type: "steam-id",
						steamId: localSteamId
					}));
				} else if (msg.event === "signal" && msg.type === "steam-id") {
					peerSteamId = msg.steamId || null;
					console.log("[HOST] Received peer Steam ID:", peerSteamId || "(none)");

					// Determine if we can use Steam
					const useSteam = !!(localSteamId && peerSteamId);

					// Tell client what connection type we're using
					signalingChannel.send(JSON.stringify({
						action: "signal",
						type: "connection-type",
						useSteam: useSteam,
						steamId: localSteamId
					}));

					finish({ useSteam, peerSteamId });
				}
			};

			signalingChannel.addEventListener("message", handleMessage);

			// Send steam-id immediately in case the client is already connected.
			// If we're first to connect, signal.ts delivers to nobody (no-op).
			// If client connected first, signal.ts relays it and the handshake proceeds
			// without needing the server's peer-joined (which can't reach us during $connect).
			signalingChannel.send(JSON.stringify({
				action: "signal",
				type: "steam-id",
				steamId: localSteamId
			}));
		});
	}

	clientHandshake(localSteamId: string | null, timeoutMs: number): Promise<HandshakeResult> {
		if (!this.channel) {
			return Promise.reject(new Error("Signalling channel is disposed or not connected"));
		}
		const signalingChannel = this.channel;

		return new Promise((resolve, reject) => {
			let peerSteamId: string | null = null;
			let settled = false;
			let timer: any = null;
			let handleMessage: (event: MessageEvent) => void;

			const finish = (result: HandshakeResult | null, error?: Error) => {
				if (settled) return;
				settled = true;
				if (timer !== null) clearTimeout(timer);
				signalingChannel.removeEventListener("message", handleMessage);
				if (this.pendingReject === abort) this.pendingReject = null;
				if (error) reject(error); else resolve(result!);
			};
			const abort = (error: Error) => finish(null, error);
			this.pendingReject = abort;
			timer = setTimeout(() => finish(null, new Error(`Client handshake timed out after ${timeoutMs}ms`)), timeoutMs);

			handleMessage = (event: MessageEvent) => {
				if (!this.channel) {
					console.error("WebRTCConnection: Closed signalling channel during handshake, handshake aborted");
					return;
				}
				const msg: SignalMessage = JSON.parse(event.data);

				if (msg.event === "peer-left") {
					finish(null, new Error(PEER_LEFT));
					return;
				}

				if (msg.event === "signal" && msg.type === "steam-id") {
					// Host sent their Steam ID first
					peerSteamId = msg.steamId || null;
					console.log("[CLIENT] Received host Steam ID:", peerSteamId || "(none)");

					// Send our Steam ID
					console.log("[CLIENT] Sending our Steam ID:", localSteamId || "(none)");
					signalingChannel.send(JSON.stringify({
						action: "signal",
						type: "steam-id",
						steamId: localSteamId
					}));
				} else if (msg.event === "signal" && msg.type === "connection-type") {
					// Host decided on connection type
					const useSteam = msg.useSteam === true;
					peerSteamId = msg.steamId || peerSteamId;
					console.log("[CLIENT] Host chose connection type:", useSteam ? "Steam" : "WebRTC");

					finish({ useSteam, peerSteamId });
				}
			};

			signalingChannel.addEventListener("message", handleMessage);
		});
	}

	close() {
		//Fail a waiting handshake before the socket goes, or it stays pending forever
		const reject = this.pendingReject;
		this.pendingReject = null;
		reject?.(new Error("Signalling channel closed"));
		if (this.channel) {
			this.channel.close();
			this.channel = null;
		}
	}
}